/**
 * Money Formatting Utilities
 * 
 * Formats monetary values for display based on magnitude only.
 * No reliance on stored "unit" fields - pure magnitude-based formatting.
 */

/**
 * Format a number as currency string
 * 
 * Rules:
 * - null/undefined => "Not available"
 * - abs(amount) >= 1,000,000 => show in M: $X.XXM
 * - abs(amount) >= 1,000 => show in K: $X.XXK  
 * - Else show full: $X.XX (or integer if whole number)
 * 
 * Never apply display scaling based on stored "unit" field.
 * Only uses numeric magnitude.
 * 
 * @param {number|null} amount - The amount to format (raw number, e.g., 180000)
 * @param {string} [currency="USD"] - Currency code
 * @param {number} [decimals=2] - Number of decimal places
 * @returns {string} - Formatted string
 * 
 * Examples:
 *   formatMoney(180000) => "$180.00K"
 *   formatMoney(1800000) => "$1.80M"
 *   formatMoney(0.18) => "$0.18"
 *   formatMoney(180000, 'USD', 0) => "$180K"
 *   formatMoney(null) => "Not available"
 */
export function formatMoney(amount, currency = 'USD', decimals = 2) {
  if (amount === null || amount === undefined || Number.isNaN(amount)) {
    return 'Not available';
  }

  const absAmount = Math.abs(amount);
  const isNegative = amount < 0;
  const sign = isNegative ? '-' : '';

  // Currency symbols
  const symbols = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    CAD: 'C$',
    AUD: 'A$',
    UNKNOWN: ''
  };
  const symbol = symbols[currency] || symbols.USD;

  // Format based on magnitude
  let formatted;
  let suffix = '';

  if (absAmount >= 1000000000) {
    // Billions
    formatted = (absAmount / 1000000000).toFixed(decimals);
    suffix = 'B';
  } else if (absAmount >= 1000000) {
    // Millions
    formatted = (absAmount / 1000000).toFixed(decimals);
    suffix = 'M';
  } else if (absAmount >= 1000) {
    // Thousands
    formatted = (absAmount / 1000).toFixed(decimals);
    suffix = 'K';
  } else {
    // Full amount
    formatted = absAmount.toFixed(decimals);
  }

  // Remove trailing zeros and unnecessary decimal point
  // but keep at least one decimal if decimals > 0
  if (decimals > 0) {
    formatted = formatted.replace(/\.?0+$/, '');
    if (formatted.endsWith('.')) {
      formatted = formatted.slice(0, -1);
    }
    // If all decimals were removed, add one zero
    if (!formatted.includes('.') && decimals > 0) {
      formatted += '.0';
    }
  }

  return `${sign}${symbol}${formatted}${suffix}`;
}

/**
 * Format money with compact notation (shorter format)
 * @param {number|null} amount
 * @param {string} [currency="USD"]
 * @returns {string}
 */
export function formatMoneyCompact(amount, currency = 'USD') {
  if (amount === null || amount === undefined || Number.isNaN(amount)) {
    return 'N/A';
  }

  const absAmount = Math.abs(amount);
  const isNegative = amount < 0;
  const sign = isNegative ? '-' : '';

  const symbols = { USD: '$', EUR: '€', GBP: '£', UNKNOWN: '' };
  const symbol = symbols[currency] || '$';

  let formatted;
  let suffix = '';

  if (absAmount >= 1000000000) {
    formatted = (absAmount / 1000000000).toFixed(1);
    suffix = 'B';
  } else if (absAmount >= 1000000) {
    formatted = (absAmount / 1000000).toFixed(1);
    suffix = 'M';
  } else if (absAmount >= 1000) {
    formatted = (absAmount / 1000).toFixed(1);
    suffix = 'K';
  } else {
    formatted = Math.round(absAmount).toString();
  }

  // Remove trailing .0
  formatted = formatted.replace(/\.0$/, '');

  return `${sign}${symbol}${formatted}${suffix}`;
}

/**
 * Format money with full precision (no K/M abbreviation)
 * @param {number|null} amount
 * @param {string} [currency="USD"]
 * @param {number} [decimals=2]
 * @returns {string}
 */
export function formatMoneyFull(amount, currency = 'USD', decimals = 2) {
  if (amount === null || amount === undefined || Number.isNaN(amount)) {
    return 'Not available';
  }

  const isNegative = amount < 0;
  const sign = isNegative ? '-' : '';

  const symbols = { USD: '$', EUR: '€', GBP: '£', UNKNOWN: '' };
  const symbol = symbols[currency] || '$';

  const absAmount = Math.abs(amount);
  
  // Add thousands separators
  const parts = absAmount.toFixed(decimals).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const formatted = parts.join('.');

  return `${sign}${symbol}${formatted}`;
}

/**
 * Get the appropriate scale suffix based on magnitude
 * @param {number} amount
 * @returns {string} - "B", "M", "K", or ""
 */
export function getScaleSuffix(amount) {
  const absAmount = Math.abs(amount);
  if (absAmount >= 1000000000) return 'B';
  if (absAmount >= 1000000) return 'M';
  if (absAmount >= 1000) return 'K';
  return '';
}

/**
 * Format a range of money values
 * @param {number} min
 * @param {number} max
 * @param {string} [currency="USD"]
 * @returns {string}
 */
export function formatMoneyRange(min, max, currency = 'USD') {
  if ((min === null || min === undefined) && (max === null || max === undefined)) {
    return 'Not available';
  }

  const minStr = formatMoneyCompact(min, currency);
  const maxStr = formatMoneyCompact(max, currency);

  if (min === null || min === undefined) return `Up to ${maxStr}`;
  if (max === null || max === undefined) return `From ${minStr}`;

  return `${minStr} – ${maxStr}`;
}

export default {
  formatMoney,
  formatMoneyCompact,
  formatMoneyFull,
  getScaleSuffix,
  formatMoneyRange
};
